import {useCallback, useMemo, useState} from "react";
import styled from "styled-components";
import {Report} from "../../../../../../../data/models/Report";
import {useAppStore} from "../../../../../../../StateManager";
import {PopupV5} from "../../../../../popup/PopupV5";
import {FieldV5} from "../../../../../details/FieldV5";
import {Permission, XyiconFeature} from "../../../../../../../generated/api/base";
import {ReportType} from "../../../../../../../generated/api/reports";
import {ButtonStyled, ButtonV5} from "../../../../../button/ButtonV5";
import EyeIcon from "../../../../../icons/eye.svg?react";
import GlobeIcon from "../../../../../icons/globe.svg?react";
import UsersIcon from "../../../../../icons/users.svg?react";
import UserIcon from "../../../../../icons/user.svg?react";
import ClipboardTextIcon from "../../../../../icons/clipboard-text.svg?react";
import InfoIcon from "../../../../../icons/circle-information.svg?react";
import PenIcon from "../../../../../icons/pen-new.svg?react";
import {TextInputV5} from "../../../../../details/datatypes/TextInputV5";
import {baseDistance, ELLIPSIS, Flex, FlexCenter, FlexCenterStyle, radius, VerticalFlexStyle} from "../../../../../styles/styles";
import {FieldIconStyled, FieldStyled} from "../../../../../details/Field.styled";
import {colorPalette} from "../../../../../styles/colorPalette";
import {GlobalSharingPopup} from "../../../../../sharing/GlobalSharingPopup";

interface IRunReportDetailsProps {
	readonly report: Report;
	readonly onClose: () => void;
}

export const RunReportDetailsV5 = (props: IRunReportDetailsProps) => {
	const appState = useAppStore((state) => state.appState);
	const {report, onClose} = props;
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [name, setName] = useState<string>(report.name);
	const [description, setDesription] = useState<string>(report.description);
	const [globalSharingPopupOpen, setGlobalSharingPopupOpen] = useState<boolean>(false);

	const getErrorMessage = useCallback(
		(value: string) => {
			const isNameValid = appState.actions.isNameValidForViewOrReport(value, XyiconFeature.Report, report.id, true);

			if (isNameValid) {
				return "";
			}
			return value.trim() === "" ? "Name cannot be empty!" : "Name needs to be unique!";
		},
		[appState, report.id],
	);

	const onSaveChanges = async () => {
		report.name = name;
		report.description = description;

		setIsLoading(true);
		await report.update();
		onClose();
		setIsLoading(false);
	};

	const onGlobalSharingPopupClose = () => {
		setIsLoading(false);
		setGlobalSharingPopupOpen(false);
		onClose();
	};

	const owner = report.isGlobal
		? {
				icon: GlobeIcon,
				value: "Global",
			}
		: {
				icon: appState.user.id === report.ownedBy ? UserIcon : UsersIcon,
				value: appState.actions.renderName(report.ownedBy),
			};

	const canEditReport = Report.getPermission(report, appState) > Permission.View;
	const permission = canEditReport ? {icon: PenIcon, value: "Edit"} : {icon: EyeIcon, value: "View Only"};

	const permissionTooltip = report.isGlobal
		? "All Administrators have edit permissions"
		: canEditReport
			? "You have permission to make changes to this report"
			: "Contact the owner to make changes to this report";

	const saveButtonDisabled = useMemo(() => {
		let bool = false;

		if (isLoading || getErrorMessage(name) !== "" || (report.name === name && report.description === description) || !canEditReport) {
			bool = true;
		}

		return bool;
	}, [isLoading, canEditReport, name, description, getErrorMessage, report]);

	return (
		<>
			{globalSharingPopupOpen ? (
				<GlobalSharingPopup
					onClose={onGlobalSharingPopupClose}
					newName={name}
					onConfirm={onGlobalSharingPopupClose}
					viewActionType="rename"
					isReports={true}
					viewItems={[report]}
				/>
			) : (
				<PopupV5
					onClose={onClose}
					label={
						canEditReport ? (
							<TextInputV5
								value={name}
								onInput={setName}
								getErrorMessage={getErrorMessage}
								style={{maxWidth: 335}}
							/>
						) : (
							<ReadOnlyReportName title={name}>{name}</ReadOnlyReportName>
						)
					}
					centerOnScreen={true}
					noButtons={true}
					width="744px"
					height="400px"
					freezeRoot={true}
					closeOnClickOutside={false}
				>
					<Flex
						$gap={baseDistance.md}
						$flex="1"
					>
						<Description>
							<ClipboardTextIcon style={{zIndex: 9999, position: "absolute", top: 76, left: 26}} />
							<TextInputV5
								value={description}
								isTextArea={true}
								onInput={setDesription}
								disabled={!canEditReport}
							/>
						</Description>
						<Details>
							<FlexCenter
								$gap={baseDistance.sm}
								style={{top: 22, position: "absolute"}}
							>
								<InfoIcon
									width={18}
									height={18}
								/>
								Report Details
							</FlexCenter>
							<FieldV5 label="Report ID">{report.refId}</FieldV5>
							<FieldV5 label="Module">{report.itemFeature === XyiconFeature.Xyicon ? "Xyicon" : "Boundary"}</FieldV5>
							<FieldV5 label="Category">{report.type === ReportType.CustomReport ? "Custom Report" : "User Defined"}</FieldV5>
							<FieldV5 label="Owner">
								<FieldV5
									label={owner.value}
									icons={{preLabelIcon: owner.icon, initialPositionedPreLabelIcon: true}}
								/>
							</FieldV5>
							<FieldV5 label="Permissions">
								<FieldV5
									label={permission.value}
									icons={{preLabelIcon: permission.icon, initialPositionedPreLabelIcon: true}}
									tooltips={{preLabelIconTooltip: permissionTooltip}}
								/>
							</FieldV5>
							{canEditReport && (
								<ButtonV5
									className="primary"
									label={`${isLoading ? "Saving..." : "Save Changes"}`}
									title={`${isLoading ? "Saving..." : "Save Changes"}`}
									onClick={() => (report.isGlobal && name !== report.name ? setGlobalSharingPopupOpen(true) : onSaveChanges())}
									disabled={saveButtonDisabled || globalSharingPopupOpen}
								/>
							)}
						</Details>
					</Flex>
				</PopupV5>
			)}
		</>
	);
};

const Description = styled.div`
	${VerticalFlexStyle};
	flex: 1;
	align-self: stretch;

	textarea {
		flex: 1;
		padding-left: ${baseDistance.lg};
		border-radius: ${radius.md};
		border-color: ${colorPalette.gray.c300};

		&:focus {
			border-color: ${colorPalette.primary.c500Primary};
		}
	}
`;

const ReadOnlyReportName = styled.div`
	display: block;
	max-width: 335px;
	padding: 0 ${baseDistance.sm};
	border: 1px solid transparent;
	border-radius: ${radius.sm};
	font-size: 24px;
	line-height: 32px;
	font-weight: bold;
	${ELLIPSIS};
`;

const Details = styled.div`
	${VerticalFlexStyle};
	gap: ${baseDistance.md};
	flex: 1;

	${ButtonStyled} {
		align-self: flex-end;
		margin-top: auto;
	}

	${FieldStyled} {
		${FlexCenterStyle};
		gap: ${baseDistance.sm};

		${FieldIconStyled} {
			min-width: ${baseDistance.md};
			height: ${baseDistance.md};

			path {
				stroke-width: 1;
			}
		}
	}
`;
